import { useEffect, useRef } from "react";
import { gql, useQuery } from "@apollo/client";
import { useStore } from "../../store";

const GET_ZONES = gql`
  query GetZonesAlarm { defenseZones { id type geometryJson active } }
`;
const GET_WL = gql`
  query GetWLAlarm { whitelist { droneId } }
`;

function pip(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

export default function AlarmEngine() {
  const drones           = useStore(s => s.drones);
  const soundEnabled     = useStore(s => s.soundEnabled);
  const setAlarmedDrones = useStore(s => s.setAlarmedDrones);
  const { data: zonesData } = useQuery(GET_ZONES, { pollInterval: 5000 });
  const { data: wlData }    = useQuery(GET_WL,    { pollInterval: 30000 });

  const ctxRef   = useRef<AudioContext | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const alarmRef = useRef<"WARNING" | "DEFENSE" | null>(null);
  const entryRef = useRef(0);
  const sndRef   = useRef(soundEnabled);
  useEffect(() => { sndRef.current = soundEnabled; }, [soundEnabled]);
  const prevMap  = useRef<Map<string, "WARNING" | "DEFENSE">>(new Map());

  useEffect(() => {
    const unlock = () => {
      if (!ctxRef.current) ctxRef.current = new AudioContext();
      if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    };
    document.addEventListener("click",   unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      document.removeEventListener("click",   unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  const getCtx = (): AudioContext => {
    if (!ctxRef.current) ctxRef.current = new AudioContext();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };

  const beep = (freq: number, dur: number, type: OscillatorType, vol: number) => {
    try {
      const c = getCtx();
      if (c.state !== "running") return;
      const o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(vol, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
      o.start(); o.stop(c.currentTime + dur);
    } catch {}
  };

  const stopAlarm = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    alarmRef.current = null;
  };

  const startAlarm = (type: "WARNING" | "DEFENSE") => {
    if (alarmRef.current === type) return;
    stopAlarm();
    alarmRef.current = type;
    entryRef.current = Date.now();
    if (type === "WARNING") {
      timerRef.current = setInterval(() => {
        if (!sndRef.current) return;
        const t = Math.min((Date.now() - entryRef.current) / 60000, 1);
        beep(700 + t * 500, 0.08, "sine", 0.25);
      }, 700);
    } else {
      let ph = 0;
      timerRef.current = setInterval(() => {
        if (!sndRef.current) return;
        beep(ph++ % 2 === 0 ? 1300 : 900, 0.18, "sawtooth", 0.4);
      }, 300);
    }
  };

  useEffect(() => {
    if (!zonesData?.defenseZones) return;

    const wlIds = new Set<string>((wlData?.whitelist || []).map((w: any) => w.droneId));

    const zones = (zonesData.defenseZones as any[])
      .filter((z: any) => z.active)
      .map((z: any) => {
        try {
          const geo = typeof z.geometryJson === "string" ? JSON.parse(z.geometryJson) : z.geometryJson;
          return { type: z.type as "WARNING" | "DEFENSE", ring: geo.coordinates[0] as [number, number][] };
        } catch { return null; }
      }).filter((z: any): z is { type: "WARNING" | "DEFENSE"; ring: [number, number][] } => z !== null);

    const alarmed = new Map<string, "WARNING" | "DEFENSE">();
    drones.forEach((drone: any, id: string) => {
      if (wlIds.has(id)) return; // skip whitelisted
      if (drone.lng == null || drone.lat == null) return;
      for (const z of zones) {
        if (pip(Number(drone.lng), Number(drone.lat), z.ring)) {
          const prev = alarmed.get(id);
          if (z.type === "DEFENSE" || !prev) alarmed.set(id, z.type);
        }
      }
    });

    const changed = alarmed.size !== prevMap.current.size ||
      [...alarmed.entries()].some(([k, v]) => prevMap.current.get(k) !== v);
    if (changed) { prevMap.current = alarmed; setAlarmedDrones(alarmed); }

    let overall: "WARNING" | "DEFENSE" | null = null;
    alarmed.forEach((s: string) => { if (s === "DEFENSE" || !overall) overall = s as any; });

    if (!overall) { stopAlarm(); return; }
    if (soundEnabled) startAlarm(overall); else stopAlarm();
  }, [drones, zonesData, wlData, soundEnabled]);

  useEffect(() => { if (!soundEnabled) stopAlarm(); }, [soundEnabled]);
  useEffect(() => () => stopAlarm(), []);
  return null;
}
